<script lang="ts" setup>
import LineInfoCircle from '@docue/docue-ui-v2/icons/LineInfoCircle.vue'

defineOptions({
  inheritAttrs: false,
})

const { t } = useI18n()
const { country } = useAuth()
const { priceList, promotedPlan, showIntercom, notifyContactFormTracker } = useSubscriptionProcessContext()

const isPriceListV1_1 = computed(() => priceList.data.value?.data_version === 'v1.1')

const isPending = computed(() => priceList.isPending.value)
const isError = computed(() => priceList.isError.value)

const priceListIsPendingDelayed = refDelayed(isPending, false)

const priceListItems = computed(() => (priceList.dataWithoutUpgrades.value?.items ?? []))

const isShowAllClicked = ref(false)

const { translateForCountry } = useCountryAwareTranslation()

const allFeatures = computed(() =>
  [
    ...(priceList.dataWithoutUpgrades.value?.data?.features?.filter(({ only_featured }) => !only_featured) ?? []),
    {
      // Hack to add legal-guarantee feature for PS-318
      id: 'legal-guarantee',
      title: t('subscribe.plans.legal-guarantee.title'),
      description: translateForCountry('subscribe.legal-guarantee.description'),
    },
  ],
)

const { isActive: isLegalGuaranteeTestActive } = useTest('legal-guarantee')

const promotedFeatures = computed(() => {
  const arr = [...(priceList.dataWithoutUpgrades.value?.data?.promoted_features ?? [])]

  if (isLegalGuaranteeTestActive.value) {
    // Hack to add legal-guarantee feature for PS-318
    const indexToInsert = country.value === 'FI' ? 2 : 3
    arr.splice(indexToInsert, 0, 'legal-guarantee')
  }

  return arr
})

const featureList = computed(() => {
  // Sort all features to have promoted features first
  const sortedFeatures = [...allFeatures.value].sort((a, b) => {
    const aIndex = promotedFeatures.value.indexOf(a.id)
    const bIndex = promotedFeatures.value.indexOf(b.id)
    if (aIndex === -1 && bIndex === -1)
      return 0
    if (aIndex === -1)
      return 1
    if (bIndex === -1)
      return -1
    return aIndex - bIndex
  })

  return isShowAllClicked.value
    ? sortedFeatures
    : promotedFeatures.value.length
      ? sortedFeatures.slice(0, promotedFeatures.value.length)
      : sortedFeatures.slice(0, isPriceListV1_1.value ? 5 : 8)
})

// Filter out legal-guarantee feature accordingly for PS-318
const visibleFeatures = computed(() => featureList.value.filter(({ id }) => isLegalGuaranteeTestActive.value || id !== 'legal-guarantee'))

const isContactUsModalOpen = ref(false)
</script>

<template>
  <LazyContactUsModal
    v-if="isContactUsModalOpen"
    v-model="isContactUsModalOpen"
    @notify-child-app-tracker="notifyContactFormTracker"
  />
  <div
    class="w-full overflow-x-auto overscroll-x-none @container md:overflow-x-visible"
    v-bind="$attrs"
  >
    <div
      v-if="priceListIsPendingDelayed"
      class="flex h-[576px] items-center justify-center rounded-xl border border-gray-200"
    >
      <DTLoaderDots />
    </div>
    <div
      v-else-if="isError"
      class="flex h-[576px] items-center justify-center rounded-xl border border-gray-200"
    >
      <div class="max-w-sm text-pretty text-sm text-gray-600">
        {{ t('subscribe.plans.not-available') }}
        <DTButton
          variant="link"
          size="sm"
          dense
          @click="showIntercom"
        >
          {{ t('contact-us') }}
        </DTButton>
      </div>
    </div>
    <template v-else>
      <table
        class="h-px w-[800px] table-fixed border-separate border-spacing-0 @2xl:w-full"
      >
        <thead class="align-top">
          <tr class="h-full divide-x divide-gray-200">
            <th
              class="
                  w-1/4
                  rounded-tl-xl
                  border-y
                  border-l
                  border-gray-200
                  bg-white
                  px-5
                  py-6
                  "
            />

            <SubscriptionPlansTableItemHeading
              v-for="item in priceListItems"
              :key="item.id"
              :item
              class="
                  sticky
                  top-0
                  z-10
                  w-1/4
                  border-y
                  border-gray-200
                  px-2
                  py-6
                  last:rounded-tr-xl
                  last:!border-r
                  @3xl:px-5
                  "
              :class="promotedPlan?.id === item.id ? 'bg-blue-25' : 'bg-white'"
              @contact-requested="isContactUsModalOpen = true"
            />
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="feature in visibleFeatures"
            :key="feature.id"
            :class="{
              'last:[&_td]:last:rounded-br-xl first:[&_td]:last:rounded-bl-xl': isShowAllClicked,
            }"
          >
            <th
              scope="row"
              class="
                  border-b
                  border-l
                  border-gray-200
                  px-5
                  py-2
                  text-left
                  text-xs
                  font-normal
                  text-gray-600
                  "
            >
              <span>
                {{ feature.title }}
                <DTTooltip
                  v-if="feature.description"
                  :content="feature.description"
                  class="inline-block align-text-top"
                >
                  <LineInfoCircle class="size-3.5" />
                </DTTooltip>
              </span>
            </th>

            <SubscriptionPlansTableItemCell
              v-for="item in priceListItems"
              :key="item.id"
              :feature
              :item
              class="
                  border-b
                  border-l
                  border-gray-200
                  px-5
                  py-2
                  text-center
                  text-sm
                  font-normal
                  last:border-r
                  "
              :class="promotedPlan?.id === item.id ? 'bg-blue-25' : 'bg-white'"
            />
          </tr>
        </tbody>
      </table>
      <div
        v-if="!isShowAllClicked"
        class="
            sticky
            left-0
            w-full
            rounded-b-xl
            border
            border-t-0
            border-gray-200
            text-center
            "
      >
        <DTButton
          variant="link"
          @click="isShowAllClicked = true"
        >
          {{ t('show-more') }}
        </DTButton>
      </div>
    </template>
  </div>
</template>
